import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { Symbol } from './Symbol';
import { WinResult } from './Paylines';
import { ParticleSystem } from '../components/ParticleSystem';
import { AudioManager } from '../utils/AudioManager';
import { COLORS } from '../config/Colors';

export class WinAnimator {
  private container: Container;
  private particleContainer: Container;
  private particleSystem: ParticleSystem;
  private winText: Text;
  private audioManager: AudioManager;
  
  constructor(container: Container) {
    this.container = container;
    this.audioManager = AudioManager.getInstance();
    
    this.particleContainer = new Container();
    this.particleContainer.zIndex = 200;
    this.container.addChild(this.particleContainer);
    
    this.particleSystem = new ParticleSystem(this.particleContainer);
    
    const style = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 56,
      fontWeight: 'bold',
      fill: COLORS.WIN_GOLD,
      stroke: { color: 0x000000, width: 6 },
      dropShadow: {
        color: 0xFF6B35,
        blur: 12,
        distance: 6
      }
    });
    
    this.winText = new Text({ text: '', style });
    this.winText.anchor.set(0.5);
    this.winText.x = 0;
    this.winText.y = -400;
    this.winText.zIndex = 201;
    this.winText.visible = false;
    this.container.addChild(this.winText);
  }
  
  public async animateWin(
    results: WinResult[],
    reels: Container[],
    totalWin: number
  ): Promise<void> {
    if (results.length === 0) return;
    
    const symbols: Symbol[] = [];
    results.forEach(result => {
      result.positions.forEach(pos => {
        const reel = reels[pos.reel] as any;
        const symbol = reel.getSymbolAt ? reel.getSymbolAt(pos.row) : null;
        if (symbol && !symbols.includes(symbol)) {
          symbols.push(symbol);
        }
      });
    });
    
    symbols.forEach(symbol => {
      symbol.highlight();
    });
    
    await this.animateWinText(totalWin);
    
    if (totalWin >= 500) {
      await this.createBigWinEffect(totalWin);
    } else if (totalWin >= 100) {
      await this.createMediumWinEffect(totalWin);
    } else {
      await this.createSmallWinEffect(totalWin);
    }
    
    symbols.forEach(symbol => {
      symbol.reset();
    });
  }
  
  private async animateWinText(amount: number): Promise<void> {
    const prefix = amount >= 500 ? '🎉 BIG WIN!' : amount >= 100 ? '⭐ WIN!' : '✨ WIN!';
    this.winText.text = `${prefix} ${amount.toLocaleString()}`;
    this.winText.visible = true;
    this.winText.alpha = 0;
    this.winText.scale.set(0.5);
    
    const duration = 500;
    const startTime = Date.now();
    
    return new Promise(resolve => {
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        this.winText.alpha = progress;
        this.winText.scale.set(0.5 + progress * 0.5);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setTimeout(() => {
            this.winText.visible = false;
            resolve();
          }, 1500);
        }
      };
      
      animate();
    });
  }
  
  private async createSmallWinEffect(amount: number): Promise<void> {
    const x = 0;
    const y = 0;
    
    this.particleSystem.burst(x, y, 'small');
    
    return new Promise(resolve => {
      setTimeout(resolve, 1000);
    });
  }
  
  private async createMediumWinEffect(amount: number): Promise<void> {
    const x = 0;
    const y = 0;
    
    this.particleSystem.burst(x, y, 'medium');
    
    return new Promise(resolve => {
      setTimeout(resolve, 1500);
    });
  }
  
  private async createBigWinEffect(amount: number): Promise<void> {
    const x = 0;
    const y = 0;
    
    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        this.particleSystem.burst(x, y, 'big');
      }, i * 200);
    }
    
    return new Promise(resolve => {
      setTimeout(resolve, 2000);
    });
  }
  
  public update(delta: number): void {
    this.particleSystem.update(delta);
  }
  
  public clear(): void {
    this.particleSystem.clear();
    this.winText.visible = false;
  }
}